import { forwardRef, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Transition } from '@headlessui/react';

import { Icon, type IconName } from '@lib/ui';
import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const alertVariants = cva(
  [
    // Layout
    'relative rounded-lg',
  ],
  {
    variants: {
      variant: {
        filled: [
          'p-4',
          'bg-this-solid', // Background
          'text-white', // Text
        ],
        outlined: [
          'p-4',
          'border border-this-line', // Border
          'bg-transparent', // Background
          'text-this-text', // Text
        ],
        soft: [
          'p-4',
          'bg-this-bg', // Background
          'text-this-text', // Text
        ],
        extended: [
          'flex overflow-hidden bg-canvas',
          'text-this-text', // Text
        ],
      },
    },
    defaultVariants: {
      variant: 'soft',
    },
  }
);

const defaultIconMap: Partial<Record<ColorType, IconName>> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

type AlertProps = {
  title?: string;
  children?: ReactNode;
  color?: ColorType;
  icon?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  actions?: ReactNode;
} & VariantProps<typeof alertVariants> &
  ComponentPropsWithoutRef<'div'>;

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      title,
      children,
      color = 'info',
      variant,
      icon,
      dismissible = false,
      onDismiss,
      actions,
      className,
      ...rest
    },
    ref
  ) => {
    const [dismissed, setDismissed] = useState(false);

    const handleDismiss = () => {
      setDismissed(true);
      onDismiss?.();
    };

    const defaultIconName = defaultIconMap[color];
    // icon={undefined} → use default, icon={null} → no icon, icon={<X/>} → custom icon
    const displayIcon =
      icon === undefined ? defaultIconName ? <Icon name={defaultIconName} /> : null : icon;

    const isExtended = variant === 'extended';

    const content = isExtended ? (
      <>
        <div className="w-1.5 shrink-0 bg-this-solid" />
        <div className="flex-1 bg-this-bg px-4 py-3">
          {title && <h2 className="text-lg font-semibold uppercase">{title}</h2>}
          {children && <p className={cn(title && 'mt-2')}>{children}</p>}
          {actions && <div className="mt-3 flex gap-2">{actions}</div>}
        </div>
      </>
    ) : (
      <div className="flex gap-3">
        {displayIcon && <div className="shrink-0">{displayIcon}</div>}
        <div className="min-w-0 flex-1">
          {title && <h5 className="font-medium">{title}</h5>}
          {children && <div className={cn('text-sm opacity-90', title && 'mt-1')}>{children}</div>}
          {actions && <div className="mt-3 flex gap-2">{actions}</div>}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 cursor-pointer rounded p-1 opacity-70 transition-opacity hover:opacity-100"
          >
            <Icon name="close" />
          </button>
        )}
      </div>
    );

    return (
      <Transition
        show={!dismissed}
        leave="duration-200 transition ease-in-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          ref={ref}
          role="alert"
          className={cn(alertVariants({ variant }), setThisClass(color), className)}
          {...rest}
        >
          {content}
        </div>
      </Transition>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert };
export type { AlertProps };
